import React, { useState, useEffect } from "react";
import { Constants, DOG_OWNER_PREMIUM_ENABLED } from "@eagerdog/constants";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import InfiniteScroll from 'react-infinite-scroll-component';
import { Helmet } from "react-helmet";

import { IUser, IEvent, IEventFullnessType, IClub } from "@eagerdog/interfaces";

import { helperService } from "../../services/helper.service";
import { apiService } from "../../services/api.service";
import { IEventSummary } from "@eagerdog/interfaces";

import { Modal, useModal } from "../../components/Modal/Modal";
import EventCard from "../../components/EventCard/EventCard";
import Congrats from "../../components/Congrats/Congrats";
import EventForm from "../../components/StepForm/EventForm/EventForm";
import SearchFilter, { ISearchOption, IActiveFilter } from "src/components/SearchFilter/SearchFilter";
import { States, Provinces } from "src/components/Dropdown/Dropdown";
import StripeOnboardingModal from "../../components/StripeOnboardingModal/StripeOnboardingModal";

import "./Events.scss";
import { toast } from "../../components/Toast/ToastManager";

interface IProps {}

const Events: React.FC<IProps> = (props) => {
  const user:IUser = helperService.getUser();
  const navigate = useNavigate();
  const [discoverParams] = useSearchParams();
  const [dateFilter] = useState<Date | null>(() => {
    const d = discoverParams.get("date");
    return d ? new Date(d) : null;
  });

  const { isShown, toggle } = useModal();

  const limit:number = 12;

  const [events, setEvents] = useState<IEventSummary[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [skip, setSkip] = useState<number>(0);
  const [fullnessEvents, setFullnessEvents] = useState<IEventFullnessType[]>([]);
  const [currentClub, setCurrentClub] = useState<IClub | null>(null);

  const [modalContent, setModalContent] = useState<string>();
  const [newEventUrl, setNewEventUrl] = useState<string>("");
  const [searchFilters, setSearchFilters] = useState<IActiveFilter[]>([]);
  const [searchReset, setSearchReset] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const pModalContent = helperService.usePrevious(modalContent);

  const loadClubData = async () => {
    if (user.user_type === Constants.user_type.club_manager) {
      apiService.getCurrentClub().then((club) => {
        setCurrentClub(club);
        // Trigger events loading once we have club info
        setSearchReset(true);
      }).catch((error) => {
        console.error('Failed to get current club:', error);
      });
    } else {
      setSearchReset(true)
    }
  };

  // Get current club for club managers
  useEffect(() => {
    loadClubData().then()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.user_type]);

  const getEvents = () => {
    setLoading(true);

    let query: any = {
      sort: [
          {
              "attribute_name": "start_date",
              "sort": "asc"
          }
      ],
      $limit: limit,
      $skip: skip,
      query: {
        $and: [],
        $or: []
      }
    };

    for (const filter of searchFilters) {
    let newQuery: any = {
          attribute_name: filter.filter.value,
          attribute_value: {
              operator: (filter.filter.value === "start_date" || filter.filter.value === "end_date") ? "$gte" : filter.filter.value === "show_types.show_type" ? "$eq" : "$regex",
              value: filter.option
          }
      };

      if (filter.filter.value && filter.option) {
        if (newQuery.attribute_name === "sanctioning_club") {
          query.query.$and!.push(newQuery);
        } else {
          query.query.$or!.push(newQuery);
        }
      }
    }

    if (dateFilter) {
      query.query.$and!.push({
        attribute_name: "start_date",
        attribute_value: { operator: "$gte", value: dateFilter }
      });
    }

    // `end_date` is stored as midnight (event timezone) at the START of the last
    // event day, so compare against "now - 1 day" to keep an event in Upcoming
    // through its final day. Matches the public/owner endpoints in the API.
    const pastCutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);

    if (user.user_type === "club_manager") {
      // Don't make API call if we don't have club info yet
      if (!currentClub) {
        setLoading(false);
        return;
      }

      if (currentTab === 0) {
        query.query.$and!.push({
          attribute_name: "end_date",
          attribute_value: {
            operator: "$gte",
            value: pastCutoff,
          },
        });
      } else if (currentTab === 1) {
        query.query.$and!.push({
          attribute_name: "end_date",
          attribute_value: {
            operator: "$lt",
            value: pastCutoff
          }
        });

        query.sort =  [
            {
                "attribute_name": "start_date",
                "sort": "desc"
            }
        ];
      }

      apiService.getPrivateEvents(query, currentClub._id).then((events) => {
        setEvents((prevEvents) => {
          const newEvents = [...prevEvents, ...events];
          setHasMore(events.length === limit);
          setSkip(skip + limit);
          return newEvents;
        });
      }).finally(() => {
        setLoading(false);
      });
    } else if (user.user_type === Constants.user_type.owner) {
      // Dog owners - filter by upcoming/past based on tab
      if (currentTab === 0) {
        // Upcoming events - backend already filters by end_date >= yesterday
        // No need to add additional filter here
      } else if (currentTab === 1 && (!DOG_OWNER_PREMIUM_ENABLED || user.has_past_results_access)) {
        // Past events (only if user has access)
        query.query.$and!.push({
          attribute_name: "end_date",
          attribute_value: {
            operator: "$lt",
            value: pastCutoff
          }
        });

        query.sort = [
          {
            "attribute_name": "start_date",
            "sort": "desc"
          }
        ];
      }

      // Get all public past events (not just registered ones)
      if (currentTab === 1 && (!DOG_OWNER_PREMIUM_ENABLED || user.has_past_results_access)) {
        apiService.getPastPublicEvents(query).then((events) => {
          setEvents((prevEvents) => {
            const newEvents = [...prevEvents, ...events];
            setHasMore(events.length === limit);
            setSkip(skip + limit);
            return newEvents;
          });
        }).finally(() => {
          setLoading(false);
        });
      } else {
        // Upcoming - show registered events
        apiService.getOwnerEvents(query).then((events) => {
          setEvents((prevEvents) => {
            const newEvents = [...prevEvents, ...events];
            // page size could be above limit due to extra portion of owner's registered events
            setHasMore(events.length >= limit);
            setSkip(skip + limit);
            return newEvents;
          });
        }).finally(() => {
          setLoading(false);
        });
      }
    } else {
      // Logged-out users - get public events
      let newEvents: IEventSummary[] = []
      apiService.getPublicEvents(query).then((events) => {
        setEvents((prevEvents) => {
          newEvents = [...prevEvents, ...events];
          setHasMore(events.length === limit);
          setSkip(skip + limit);
          return newEvents;
        });
      }).finally(() => {
        getFullnessForEvents(newEvents.map(_event => _event._id))
      });
    }
  }

  const getFullnessForEvents = (eventIds: string[]) => {
    apiService.getFullnessEvents(eventIds).then((response) => {
      if (response.length > 0) {
        setFullnessEvents([...fullnessEvents, ...response]);
      }
    }).finally(() => {
      setLoading(false)
    });
  }

  const getModalContent = () => {
    switch (modalContent) {
      case "congratsModal":
        return <Congrats text="You made an event! Now let's create some shows for your event." url={"/e/"+ newEventUrl +"/?s=1"} urlText="Create Shows" />;
      case "stripeOnboarding":
        return (
          <StripeOnboardingModal
            clubId={currentClub?._id || ""}
            onCancel={() => {
              toggle();
              setModalContent("");
            }}
          />
        );
      case "eventForm":
        return <EventForm
          clubNumber={currentClub?.club_number as string | undefined}
          clubId={currentClub ? currentClub._id : ""} 
          onFinish={(e: IEvent) => {
            loadClubData().then(() => {
              toggle();
              navigate("/events?e=" + e.handle, { replace: true });
            })
          }}
        />
      default:
        return <></>;
    }
  }

  const setActionConfig =  (event: IEventSummary, isPastEvent: boolean = false): { text: string, link: string, disabled: boolean } => {
    let actionLink = "/e/" + event.handle;
    let actionText = "View Event";
    let actionDisabled = false;
    const isClubAdminForEvent =
      user?.user_type === Constants.user_type.club_manager ||
      helperService.hasAdmin(event.club?._id || event.club_id);

    // For past events
    if (isPastEvent) {
      // Check if user has admin access (club manager, event creator, or admin capacity for the club)
      const isClubManager = user?.user_type === Constants.user_type.club_manager;
      const isCreator = user && event.created_by === user._id;
      const hasAdminCapacity = helperService.hasAdmin(event.club?._id || event.club_id);
      
      // Club managers, creators, and users with admin capacity see "View Event" and go to normal event page
      if (isClubManager || isCreator || hasAdminCapacity) {
        actionText = "View Event";
        actionLink = "/e/" + event.handle;
      } else {
        // Regular dog owners with past results access see "View Results" and go to summary page
        actionText = "View Results";
        actionLink = "/e/" + event.handle + "/summary";
      }
      return { text: actionText, link: actionLink, disabled: actionDisabled };
    }

    if (isClubAdminForEvent) {
      if (event.status === Constants.event_status.published && event.show_types.length > 0) {
        actionLink = "/e/" + event.handle + "/register";
        actionText = "Register";
      }
    } else if (user?.user_type === "owner") {
      if (event.status === Constants.event_status.published && event.show_types.length > 0) {
        if (!event?.is_registered) {
          actionLink += "/register";
          actionText = "Register";
        }
      }
    }

    // Check if user has admin capacity for this event's club
    const hasAdminCapacity = event.admins && event.admins.includes(user._id);

    // Only disable if user is not a club manager AND does not have admin capacity
    if (user?.user_type !== "club_manager" && !hasAdminCapacity && fullnessEvents.length > 0) {
      const eventFullness = fullnessEvents.find(fe => fe._id === event._id);
      if (eventFullness && eventFullness.fullness_shows.every(show => show.is_full)) {
        actionLink = "";
        actionText = "Event Full";
        actionDisabled = true;
      }
    }

    return { text: actionText, link: actionLink, disabled: actionDisabled };
  }

  useEffect(() => {
    if (pModalContent !== modalContent) {
      if (modalContent !== "") {
        toggle();
      }
    } else {
      if (!isShown) {
        setModalContent("");
      }
    }
  }, [isShown, modalContent, pModalContent, setModalContent, toggle]);

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const newEvent = searchParams.get("e");

  useEffect(() => {
    const approveChangesParam = searchParams.get("approve_changes");

    if (approveChangesParam) {
      toast.show({
        title: "Approve Proposed Changes",
        content: `You've successfully approved proposed changes for your dog.`,
        duration: 10000,
        type: "success"
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (newEvent !== null) {
      setNewEventUrl(newEvent);
      setModalContent("congratsModal");
      window.history.pushState('', '', '/events');
    }
  }, [newEvent]);

  const searchOptions: ISearchOption[] = [
    {
        label: "Sanctioning Club",
        value: "sanctioning_club",
        helpText: "UKC, AKC, CKC, etc.",
        options: [
            "4-H", "ABI", "AHBA", "AKC", "ASFA", "BHA", "C-Wags", "CDSP", "CKC", "CPE", "GCR", "General", "ISWS", "NASDA", "PWDCA", "UKC"
        ]
    }, {
        label: "Club Name",
        value: "club.club_name",
        helpText: "Name of a Club"
    }, {
        label: "Event Name",
        value: "title",
        helpText: "Name of the Event"
    }, {
        label: "Show Type",
        value: "show_types.show_type",
        helpText: "Fetch, Fast CAT, Nosework, Rally Obedience, Conformation",
        options: [
          Constants.show_type.agility,                    // "Agility"
          Constants.show_type.att,                        // "Temperament Test (ATT)"
          Constants.show_type.barn_hunt,                  // "Barn Hunt"
          Constants.show_type.canine_good_citizen,        // "Canine Good Citizen"
          Constants.show_type.cat,                        // "CAT"
          Constants.show_type.conformation,               // "Conformation"
          Constants.show_type.country_rat_race,           // "Country Rat Race"
          Constants.show_type.dock_jumping,               // "Dock Jumping"
          Constants.show_type.farm_dog,                   // "Farm Dog"
          Constants.show_type.fast_cat,                   // "Fast CAT"
          Constants.show_type.fetch,                      // "Fetch"
          Constants.show_type.cwags_games,                // "Games" (C-WAGS)
          Constants.show_type.herding,                    // "Herding"
          Constants.show_type.pre_trial_herding,          // "Pre-Trial (Herding)"
          Constants.show_type.instinct_test_herding,      // "Instinct Test (Herding)"
          Constants.show_type.lure_coursing,              // "Lure Coursing"
          Constants.show_type.nitrous_50,                 // "Nitrous 50"
          Constants.show_type.nitrous_100,                // "Nitrous 100"
          Constants.show_type.nosework,                   // "Nosework"
          Constants.show_type.obedience,                  // "Obedience"
          Constants.show_type.precision_coursing,         // "Precision Coursing"
          Constants.show_type.rally_obedience,            // "Rally Obedience"
          Constants.show_type.scent,                      // "Scent"
          Constants.show_type.scent_detection,            // "Scent Detection" (CKC)
          Constants.show_type.scentwork,                  // "Scentwork"
          Constants.show_type.scent_dog_games,            // "Scent Dog Games"
          Constants.show_type.shed_dog,                   // "Shed Dog"
          Constants.show_type.sniff_n_go,                 // "Sniff-n-Go"
          Constants.show_type.speedway,                   // "Speedway"
          Constants.show_type.field_trial,                // "Field Trial"
          Constants.show_type.urban_rat_race,             // "Urban Rat Race"
          Constants.show_type.water_dog,                  // "Water Dog"
          Constants.show_type.weight_pull                 // "Weight Pull"
        ]
    }, {
        label: "City",
        value: "address.city",
        helpText: "City of the Event"
    }, {
        label: "State/Province",
        value: "address.state",
        helpText: "Abbreviation (ON, AL, etc.)",
        options: [...Provinces.map((s: any, index: number) => {
          return s.id;
        }), ...States.map((s: any, index: number) => {
          return s.id;
        })]
    }, {
        label: "Country",
        value: "address.country",
        helpText: "Canada, USA, etc.",
        options: ["Canada", "USA"]
    }, {
        label: "Start Date",
        value: "start_date",
        helpText: "Start Date of the Event"
    }
  ];

    const resetSearch = () => {
      setSkip(0);
      setHasMore(true);
      setEvents([]);
      setSearchReset(true);
    }

    useEffect(() => {
      resetSearch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchFilters]);

    useEffect(() => {
      if (searchReset) {
        // For club managers, only call getEvents if we have club info
        if (user.user_type === Constants.user_type.club_manager && !currentClub) {
          return;
        }
        getEvents();
        setSearchReset(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    } , [searchReset, currentClub]);

    useEffect(() => {
      resetSearch();
    }, [currentTab]);

  return (
    <div className="Events">
      <Helmet>
        <title>Events Listing</title>
        <meta name="description" content="Upcoming Fetch, Fast CAT, Nosework, Rally Obedience, and Conformation kennel club events running on Eager Dog." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="inner">
        <div className="title">
            <h1>Events</h1>
            <SearchFilter
                searchOptions={searchOptions}
                onFilterChange={(filters: IActiveFilter[]) => {
                    setSearchFilters(filters);
                }}
                initialFilters={dateFilter ? [{ filter: { label: "Start Date", value: "start_date", helpText: "Start Date of the Event" }, option: (discoverParams.get("date") ?? "") + "T00:00:00" }] : []}
            />
        </div>
        {(user.user_type === "club_manager" || (user.user_type === Constants.user_type.owner && (!DOG_OWNER_PREMIUM_ENABLED || user.has_past_results_access))) && (
          <div className="listOptions">
            <div className="tabs">
              <span className={currentTab === 0 ? "active" : ""} onClick={() => { setCurrentTab(0); }}>Upcoming</span>
              <span className={currentTab === 1 ? "active" : ""} onClick={() => { setCurrentTab(1); }}>Past</span>
            </div>
          </div>
        )}
        {((!loading && skip === 0) || skip > 0) && <InfiniteScroll
          className="list"
          dataLength={events.length}
          next={getEvents}
          hasMore={hasMore}
          loader={<div className="loading" key={0}></div>}
        >
          {events.length === 0 && user?.user_type !== Constants.user_type.club_manager ? (
            <div className="no-events">
              <span>{currentTab === 1 ? "There aren't any past events to display." : "There aren't any upcoming events at the moment."}</span>
            </div>
          ) : (
            events.map((event: IEventSummary, i: number) => {
              const isPastEvent = currentTab === 1;
              const actionConfig = setActionConfig(event, isPastEvent);
              return(
                <EventCard
                  event={event}
                  key={"event-" + i}
                  action={actionConfig}
                  isAdmin={user?.user_type === "club_manager"}
                  isPastEvent={isPastEvent}
                  afterUpdate={resetSearch}
                />
              );
            })
          )}
          {user?.user_type === Constants.user_type.club_manager && currentTab === 0 && (
            <div onClick={async () => { 
              if (currentClub?._id) {
                try {
                  const status = await apiService.getAccountStatus(currentClub._id);
                  if (status.onboarding_status !== "requirements_met" || !status.charges_enabled) {
                    setModalContent("stripeOnboarding");
                    return;
                  }
                } catch (err) {
                  console.error("Failed to fetch account status:", err);
                }
              }
              setModalContent("eventForm"); 
            }} className="createCard">
              <div className="icon plus"></div>
              <span>Create an Event</span>
            </div>
          )}
          {user?.user_type === Constants.user_type.club_manager && currentTab === 0 && (events.length + 1) % 3 !== 0 && <div className="eventCardPlaceholder"></div>}
          {user?.user_type === Constants.user_type.club_manager && currentTab === 1 && events.length % 3 !== 0 && <div className="eventCardPlaceholder"></div>}
          {user?.user_type !== Constants.user_type.club_manager && events.length % 3 !== 0 && <div className="eventCardPlaceholder"></div>}
        </InfiniteScroll>}
        {(loading && skip === 0) && <div className="loadingWrap list">
          <div className="eventCard"></div>
          <div className="eventCard"></div>
          <div className="eventCard"></div>
          <div className="eventCard"></div>
          <div className="eventCard"></div>
          <div className="eventCard"></div>
        </div>}
      </div>
      <Modal
        className={modalContent}
        isShown={isShown}
        hide={toggle}
        modalContent={getModalContent()}
      />
    </div>
  );
};

export default Events;